/* Copyright (c) 2025 Xperi. All Rights Reserved.  */

import { Credit, CreditType, Person } from 'client-code/person-content-service';

import { PersonContentServiceRepositoryImpl } from '../impls/PersonContentServiceRepositoryImpl';
import { IPersonContentService } from '../interfaces/IPersonContentService';
import { IPersonContentServiceRepository } from '../interfaces/IPersonContentServiceRepository';
import { TServicesParams } from '../types';
import { lruSet } from '../utils/LruCacheUtils';

export class PersonContentService implements IPersonContentService {
    private static cache: Map<string, Credit[]> = new Map();

    private personContentServiceRepository: IPersonContentServiceRepository;

    public constructor(params: TServicesParams) {
        this.personContentServiceRepository = new PersonContentServiceRepositoryImpl(params);
    }

    /**
     * Clears the entire cache for all credit functions
     */
    public static clearCache(): void {
        PersonContentService.cache.clear();
    }

    public async getCollectionCredits(
        applicationFeatureArea: string,
        collectionId: string,
    ): Promise<Credit[]> {
        const cacheKey = `collection-${applicationFeatureArea}-${collectionId}`;
        if (PersonContentService.cache.has(cacheKey)) {
            return PersonContentService.cache.get(cacheKey)!;
        }

        const credits = await this.personContentServiceRepository.collectionCreditsGet({
            applicationFeatureArea,
            collectionId,
        });
        lruSet(PersonContentService.cache, cacheKey, credits, 100);
        return credits;
    }

    public async getCollectionCastCredits(
        applicationFeatureArea: string,
        collectionId: string,
    ): Promise<Credit[]> {
        const cacheKey = `collection-cast-${applicationFeatureArea}-${collectionId}`;
        if (PersonContentService.cache.has(cacheKey)) {
            return PersonContentService.cache.get(cacheKey)!;
        }

        const credits = await this.personContentServiceRepository.collectionCreditsGet({
            applicationFeatureArea,
            collectionId,
            type: CreditType.Cast,
        });
        lruSet(PersonContentService.cache, cacheKey, credits, 100);
        return credits;
    }

    public async getCollectionCrewCredits(
        applicationFeatureArea: string,
        collectionId: string,
    ): Promise<Credit[]> {
        const cacheKey = `collection-crew-${applicationFeatureArea}-${collectionId}`;
        if (PersonContentService.cache.has(cacheKey)) {
            return PersonContentService.cache.get(cacheKey)!;
        }

        const credits = await this.personContentServiceRepository.collectionCreditsGet({
            applicationFeatureArea,
            collectionId,
            type: CreditType.Crew,
        });
        lruSet(PersonContentService.cache, cacheKey, credits, 100);
        return credits;
    }

    public async getContentCredits(
        applicationFeatureArea: string,
        contentId: string,
    ): Promise<Credit[]> {
        const cacheKey = `content-${applicationFeatureArea}-${contentId}`;
        if (PersonContentService.cache.has(cacheKey)) {
            return PersonContentService.cache.get(cacheKey)!;
        }

        const credits = await this.personContentServiceRepository.contentCreditsGet({
            applicationFeatureArea,
            contentId,
        });
        lruSet(PersonContentService.cache, cacheKey, credits, 100);
        return credits;
    }

    public async getContentCastCredits(
        applicationFeatureArea: string,
        contentId: string,
    ): Promise<Credit[]> {
        const cacheKey = `content-cast-${applicationFeatureArea}-${contentId}`;
        if (PersonContentService.cache.has(cacheKey)) {
            return PersonContentService.cache.get(cacheKey)!;
        }

        const credits = await this.personContentServiceRepository.contentCreditsGet({
            applicationFeatureArea,
            contentId,
            type: CreditType.Cast,
        });
        lruSet(PersonContentService.cache, cacheKey, credits, 100);
        return credits;
    }

    public async getContentCrewCredits(
        applicationFeatureArea: string,
        contentId: string,
    ): Promise<Credit[]> {
        const cacheKey = `content-crew-${applicationFeatureArea}-${contentId}`;
        if (PersonContentService.cache.has(cacheKey)) {
            return PersonContentService.cache.get(cacheKey)!;
        }

        const credits = await this.personContentServiceRepository.contentCreditsGet({
            applicationFeatureArea,
            contentId,
            type: CreditType.Crew,
        });
        lruSet(PersonContentService.cache, cacheKey, credits, 100);
        return credits;
    }

    public async getPerson(applicationFeatureArea: string, personId: string): Promise<Person> {
        return this.personContentServiceRepository.personGet({
            applicationFeatureArea,
            personId,
        });
    }

    public async getPersonWithCollection(
        applicationFeatureArea: string,
        personId: string,
        collectionId: string,
        includeContent?: boolean,
    ): Promise<Person> {
        return this.personContentServiceRepository.personGet({
            applicationFeatureArea,
            personId,
            collectionId,
            includeContent,
        });
    }

    public async getPersonWithContent(
        applicationFeatureArea: string,
        personId: string,
        contentId: string,
        includeContent?: boolean,
    ): Promise<Person> {
        return this.personContentServiceRepository.personGet({
            applicationFeatureArea,
            personId,
            contentId,
            includeContent,
        });
    }

    public async getPersonWithCollectionAndContent(
        applicationFeatureArea: string,
        personId: string,
        collectionId: string,
        contentId: string,
        includeContent?: boolean,
    ): Promise<Person> {
        return this.personContentServiceRepository.personGet({
            applicationFeatureArea,
            personId,
            collectionId,
            contentId,
            includeContent,
        });
    }

    public async getPersonWithContentInclusion(
        applicationFeatureArea: string,
        personId: string,
        includeContent: boolean,
    ): Promise<Person> {
        return this.personContentServiceRepository.personGet({
            applicationFeatureArea,
            personId,
            includeContent,
        });
    }
}
